/**
 * MobileSubcontractorsTab
 *
 * Mobile card-based replacement for the PrimeReact DataTable in SubcontractorsTab.
 * Shows each subcontractor as a touch-friendly card with name, status chip,
 * type chip, active contracts count, key dates, and downloadable assets.
 * Supports search, type filter chips, pagination, and tap-to-open details.
 *
 * All components: MUI (@mui/material, @mui/icons-material)
 */
import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  Pagination,
  Fade,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { useSelector, useDispatch } from "react-redux";
import Langs, { Localize } from "../../../app/lang/langs";
import { getSubcontractors } from "../../../components/clients/clientsSlice";
import { downloadAsset } from "../../../assets/assetsSlice";
import MobileAddSubcontractorModal from "./MobileAddSubcontractorModal";
import MobileSubcontractorDetailsModal from "./MobileSubcontractorDetailsModal";
import ExcelManager from "../../../app/managers/excelManager";
import moment from "moment";

/* ------------------------------------------------------------------ */
/*  Style constants                                                    */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";
const TRANSITION_SPRING = "all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)";
const PAGE_SIZE = 20;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function MobileSubcontractorsTab() {
  const dispatch = useDispatch();
  const subcontractors = useSelector((state) => state.clients.subcontractors);
  const types = useSelector((state) => state.clients.providerTypes);
  const mounted = useRef(null);

  const [searchText, setSearchText] = useState("");
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null); // null = all
  const [page, setPage] = useState(1);

  const [onPreviewSubcontractor, setOnPreviewSubcontractor] = useState(null);
  const [displayDetailsModal, setDisplayDetailsModal] = useState(false);
  const [displayAddModal, setDisplayAddModal] = useState(false);

  useEffect(() => {
    if (!mounted.current) {
      dispatch(getSubcontractors());
    }
    mounted.current = true;
  }, [mounted]);

  /* ---- Filter toggles ---- */
  const toggleType = (typeValue) => {
    setSelectedTypes((prev) =>
      prev.includes(typeValue)
        ? prev.filter((t) => t !== typeValue)
        : [...prev, typeValue]
    );
    setPage(1);
  };

  const toggleStatus = (statusValue) => {
    setSelectedStatus((prev) => (prev === statusValue ? null : statusValue));
    setPage(1);
  };

  /* ---- Filtering logic ---- */
  const filteredData = useMemo(() => {
    let list = subcontractors || [];

    if (searchText.trim()) {
      const terms = searchText.toLowerCase().split(" ").filter(Boolean);
      list = list.filter((row) => {
        const searchStr = [
          row.name,
          row.status,
          row.type,
          row.email,
          row.address,
          row.vfact,
          row.reminders_comments,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return terms.every((term) => searchStr.includes(term));
      });
    }

    if (selectedStatus) {
      list = list.filter((row) => row.status === selectedStatus);
    }

    if (selectedTypes.length > 0) {
      list = list.filter((row) => selectedTypes.includes(row.type));
    }

    return list;
  }, [subcontractors, searchText, selectedStatus, selectedTypes]);

  /* ---- Reset page on filter change ---- */
  useEffect(() => {
    setPage(1);
  }, [searchText, selectedStatus, selectedTypes]);

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const paginatedData = filteredData.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  /* ---- Export ---- */
  const formatDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    return d.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const exportFilteredProviders = () => {
    let exportList = filteredData.reduce((array, current) => {
      let data = {
        Nom: current.name || "",
        Status: current.status || "",
        Type: current.type || "",
        "Contracts actifs": current.active_contracts || 0,
        "Code Fournisseur V-Fact": current.vfact || "",
        Address: current.address || "",
        "Date d'attestation URSSAF": current.date_attestation_urssaf
          ? formatDate(current.date_attestation_urssaf)
          : "",
        "Date de fin d'attestation URSSAF":
          current.date_fin_attestation_urssaf
            ? formatDate(current.date_fin_attestation_urssaf)
            : "",
        "Date d'attestation fiscale": current.date_attestation_fiscale
          ? formatDate(current.date_attestation_fiscale)
          : "",
        "Date de fin d'attestation fiscale":
          current.date_fin_attestation_fiscale
            ? formatDate(current.date_fin_attestation_fiscale)
            : "",
        "Date de fin de validité RC": current.date_fin_validite_rc
          ? formatDate(current.date_fin_validite_rc)
          : "",
        "Date d'attestation salarié étranger":
          current.date_attestation_salarie_etranger
            ? formatDate(current.date_attestation_salarie_etranger)
            : "",
        "Date de fin d'attestation salarié étranger":
          current.date_fin_attestation_salarie_etranger
            ? formatDate(current.date_fin_attestation_salarie_etranger)
            : "",
        "Dates de rappel": current.reminders_dates || "",
        "Commentaires de rappel": current.reminders_comments || "",
      };
      array.push(data);
      return array;
    }, []);
    ExcelManager.getInstance().generateAndDownloadExcelFromData(
      exportList,
      "fournisseurs"
    );
  };

  /* ---- Helpers ---- */
  const getStatusColor = (status) => {
    if (["Présent", "Actif"].includes(status)) return "#4caf50";
    return "#f44336";
  };

  const getStatusChipSx = (status) => ({
    fontWeight: 700,
    fontSize: "0.68rem",
    height: 24,
    borderRadius: "10px",
    color: "#fff",
    bgcolor: getStatusColor(status),
    flexShrink: 0,
    letterSpacing: "0.02em",
    boxShadow: ["Présent", "Actif"].includes(status)
      ? "0 2px 8px rgba(76,175,80,0.25)"
      : "0 2px 8px rgba(244,67,54,0.25)",
  });

  const isDateExpired = (dateValue) => {
    if (!dateValue) return false;
    const d = new Date(dateValue);
    return d < new Date();
  };

  const formatDisplayDate = (dateValue) => {
    if (!dateValue) return null;
    return moment(dateValue).format("DD/MM/YYYY");
  };

  /* ---- Card tap handler ---- */
  const handleCardTap = (subcontractor) => {
    setOnPreviewSubcontractor(subcontractor);
    setDisplayDetailsModal(true);
  };

  /* ---- Key dates to show on card ---- */
  const getKeyDates = (sc) => {
    const dates = [];
    const dateFields = [
      { key: "date_fin_attestation_urssaf", label: "Fin URSSAF" },
      { key: "date_fin_attestation_fiscale", label: "Fin fiscale" },
      { key: "date_fin_validite_rc", label: "Fin RC" },
    ];
    dateFields.forEach(({ key, label }) => {
      if (sc[key]) {
        dates.push({
          label,
          value: formatDisplayDate(sc[key]),
          expired: isDateExpired(sc[key]),
        });
      }
    });
    return dates;
  };

  /* ---- Status filter options ---- */
  const statusFilters = [
    { label: "Actif", value: "Actif", color: "#4caf50" },
    { label: "Inactif", value: "Inactif", color: "#f44336" },
  ];

  return (
    <Box
      sx={{
        px: 2,
        pt: 1.5,
        pb: 2.5,
        mt: 0,
        height: "calc(100vh - 130px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Search bar + action buttons */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={Localize("Search") + "..."}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "rgba(255,144,21,0.45)" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& input": { fontSize: "16px" },
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
              bgcolor: "rgba(255,255,255,0.8)",
              backdropFilter: "blur(16px) saturate(1.3)",
              WebkitBackdropFilter: "blur(16px) saturate(1.3)",
              transition: TRANSITION_SMOOTH,
              boxShadow: "0 2px 8px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,0.8)",
              "& fieldset": {
                borderColor: "rgba(255,144,21,0.08)",
                transition: TRANSITION_SMOOTH,
              },
              "&:hover fieldset": {
                borderColor: "rgba(255,144,21,0.2)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#FF9015",
                boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
              },
            },
          }}
        />
        <IconButton
          onClick={exportFilteredProviders}
          sx={{
            minWidth: 44,
            minHeight: 44,
            borderRadius: "14px",
            bgcolor: "rgba(255,255,255,0.7)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(0,0,0,0.04)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            transition: TRANSITION_SMOOTH,
            "&:hover": { bgcolor: "rgba(255,255,255,0.9)", boxShadow: "0 4px 12px rgba(0,0,0,0.06)" },
            "&:active": { transform: "scale(0.94)" },
          }}
        >
          <FileDownloadOutlinedIcon sx={{ fontSize: 22, color: "rgba(0,0,0,0.5)" }} />
        </IconButton>
        <IconButton
          onClick={() => setDisplayAddModal(true)}
          sx={{
            minWidth: 44,
            minHeight: 44,
            borderRadius: "14px",
            background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
            color: "#fff",
            boxShadow: "0 4px 14px rgba(255,144,21,0.3), 0 1px 4px rgba(255,144,21,0.15)",
            transition: TRANSITION_SMOOTH,
            "&:hover": { boxShadow: "0 6px 20px rgba(255,144,21,0.4)" },
            "&:active": { transform: "scale(0.94)" },
          }}
        >
          <AddIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Box>

      {/* Status filter chips */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 0.75 }}>
        {statusFilters.map((opt) => {
          const isActive = selectedStatus === opt.value;
          return (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              onClick={() => toggleStatus(opt.value)}
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                height: 30,
                borderRadius: "12px",
                transition: TRANSITION_SPRING,
                color: isActive ? "#fff" : opt.color,
                bgcolor: isActive ? opt.color : "rgba(255,255,255,0.6)",
                border: `1.5px solid ${isActive ? opt.color : opt.color + "40"}`,
                backdropFilter: "blur(8px)",
                boxShadow: isActive ? `0 3px 10px ${opt.color}30` : "none",
                "&:hover": {
                  bgcolor: isActive ? opt.color : `${opt.color}12`,
                },
                "&:active": { transform: "scale(0.93)" },
              }}
            />
          );
        })}
      </Box>

      {/* Type filter chips */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
        {(types || []).map((opt) => {
          const isActive = selectedTypes.includes(opt.value);
          return (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              onClick={() => toggleType(opt.value)}
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                height: 30,
                borderRadius: "12px",
                transition: TRANSITION_SPRING,
                color: isActive ? "#fff" : "rgba(0,0,0,0.55)",
                bgcolor: isActive ? "#FF9015" : "rgba(255,255,255,0.6)",
                border: isActive
                  ? "1.5px solid #FF9015"
                  : "1.5px solid rgba(0,0,0,0.1)",
                backdropFilter: "blur(8px)",
                boxShadow: isActive ? "0 3px 10px rgba(255,144,21,0.25)" : "none",
                "&:hover": {
                  bgcolor: isActive ? "#FF9015" : "rgba(255,144,21,0.06)",
                },
                "&:active": { transform: "scale(0.93)" },
              }}
            />
          );
        })}
      </Box>

      {/* Results count */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          mb: 1,
          px: 0.5,
          fontWeight: 600,
          letterSpacing: "0.03em",
          opacity: 0.55,
          fontSize: "0.72rem",
          textTransform: "uppercase",
        }}
      >
        {filteredData.length} {Localize("results")}
      </Typography>

      {/* Card list */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          pb: 2,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 5 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255,144,21,0.15)",
            borderRadius: 3,
            "&:hover": { background: "rgba(255,144,21,0.25)" },
          },
        }}
      >
        {paginatedData.map((sc, index) => {
          const keyDates = getKeyDates(sc);
          const hasExpiredDate = keyDates.some((d) => d.expired);
          const assetsCount = (sc.assets || []).length;

          return (
            <Fade in key={sc.id || index} timeout={250 + index * 50}>
              <Card
                elevation={0}
                sx={{
                  mb: 1.5,
                  borderRadius: "18px",
                  overflow: "hidden",
                  bgcolor: "rgba(255,255,255,0.88)",
                  backdropFilter: "blur(20px) saturate(1.3)",
                  WebkitBackdropFilter: "blur(20px) saturate(1.3)",
                  border: hasExpiredDate
                    ? "1px solid rgba(255,152,0,0.25)"
                    : "1px solid rgba(255,144,21,0.06)",
                  boxShadow: hasExpiredDate
                    ? "0 4px 16px rgba(255,152,0,0.08), 0 1px 4px rgba(0,0,0,0.03)"
                    : "0 4px 16px rgba(0,0,0,0.04), 0 1px 4px rgba(0,0,0,0.03), 0 0 0 0.5px rgba(255,255,255,0.5) inset",
                  transition: TRANSITION_SMOOTH,
                  "&:active": {
                    transform: "scale(0.975)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleCardTap(sc)}
                  sx={{ p: 0 }}
                >
                  <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    {/* Top row: name + status chip */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 0.75,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        {/* Avatar circle */}
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "14px",
                            background: ["Présent", "Actif"].includes(sc.status)
                              ? "linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)"
                              : "linear-gradient(135deg, #f44336 0%, #ef5350 100%)",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 800,
                            fontSize: "0.85rem",
                            flexShrink: 0,
                            boxShadow: ["Présent", "Actif"].includes(sc.status)
                              ? "0 4px 12px rgba(76,175,80,0.25)"
                              : "0 4px 12px rgba(244,67,54,0.25)",
                          }}
                        >
                          {(sc.name?.[0] || "?").toUpperCase()}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.92rem",
                              lineHeight: 1.3,
                              letterSpacing: "-0.015em",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: "rgba(0,0,0,0.85)",
                            }}
                          >
                            {sc.name}
                          </Typography>
                          {sc.email && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.73rem",
                                opacity: 0.55,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <EmailOutlinedIcon sx={{ fontSize: "0.82rem", opacity: 0.6 }} />
                              {sc.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Status chip */}
                      {sc.status && (
                        <Chip
                          label={sc.status}
                          size="small"
                          sx={getStatusChipSx(sc.status)}
                        />
                      )}
                    </Box>

                    {/* Info row: type + contracts */}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 0.75,
                        mt: 0.5,
                      }}
                    >
                      {/* Type chip */}
                      {sc.type && (
                        <Chip
                          label={sc.type}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: "0.7rem",
                            height: 24,
                            borderRadius: "10px",
                            fontWeight: 700,
                            borderColor: "rgba(255,144,21,0.3)",
                            color: "#FF9015",
                            bgcolor: "rgba(255,144,21,0.04)",
                          }}
                        />
                      )}

                      {/* Active contracts */}
                      <Chip
                        label={`${sc.active_contracts || 0} ${Localize("active_contracts")}`}
                        size="small"
                        sx={{
                          fontSize: "0.7rem",
                          height: 24,
                          borderRadius: "10px",
                          fontWeight: 700,
                          bgcolor: sc.active_contracts
                            ? "rgba(76,175,80,0.1)"
                            : "rgba(244,67,54,0.08)",
                          color: sc.active_contracts ? "#2e7d32" : "#c62828",
                          border: "none",
                        }}
                      />

                      {/* V-Fact code */}
                      {sc.vfact && (
                        <Chip
                          label={`V-Fact: ${sc.vfact}`}
                          size="small"
                          sx={{
                            fontSize: "0.68rem",
                            height: 22,
                            borderRadius: "8px",
                            fontWeight: 500,
                            bgcolor: "rgba(0,0,0,0.04)",
                            color: "rgba(0,0,0,0.45)",
                          }}
                        />
                      )}

                      {/* Assets count */}
                      {assetsCount > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.3,
                          }}
                        >
                          <DescriptionOutlinedIcon
                            sx={{
                              fontSize: "0.82rem",
                              color: "rgba(0,0,0,0.35)",
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.7rem",
                              color: "rgba(0,0,0,0.45)",
                              fontWeight: 600,
                            }}
                          >
                            {assetsCount}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Key dates row */}
                    {keyDates.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          mt: 1,
                          pt: 0.75,
                          borderTop: "1px solid rgba(0,0,0,0.04)",
                        }}
                      >
                        {keyDates.map((d, i) => (
                          <Box
                            key={i}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.4,
                              px: 0.75,
                              py: 0.25,
                              borderRadius: "8px",
                              bgcolor: d.expired ? "rgba(237,108,2,0.06)" : "transparent",
                            }}
                          >
                            {d.expired ? (
                              <WarningAmberOutlinedIcon
                                sx={{ fontSize: "0.82rem", color: "#ed6c02" }}
                              />
                            ) : (
                              <EventOutlinedIcon
                                sx={{
                                  fontSize: "0.82rem",
                                  color: "rgba(0,0,0,0.3)",
                                }}
                              />
                            )}
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.7rem",
                                color: d.expired
                                  ? "#ed6c02"
                                  : "rgba(0,0,0,0.5)",
                                fontWeight: d.expired ? 700 : 500,
                              }}
                            >
                              {d.label}: {d.value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Address */}
                    {sc.address && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mt: 0.75,
                        }}
                      >
                        <BusinessOutlinedIcon
                          sx={{
                            fontSize: "0.82rem",
                            color: "rgba(0,0,0,0.3)",
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.7rem",
                            color: "rgba(0,0,0,0.45)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontWeight: 500,
                          }}
                        >
                          {sc.address}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Fade>
          );
        })}

        {paginatedData.length === 0 && (
          <Fade in timeout={400}>
            <Box sx={{ textAlign: "center", py: 10 }}>
              <Typography
                variant="body2"
                sx={{ opacity: 0.35, fontWeight: 500, letterSpacing: "-0.01em" }}
              >
                <Langs str="No results" />
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            size="small"
            color="warning"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                transition: TRANSITION_SMOOTH,
                "&:hover": { transform: "scale(1.08)" },
                "&:active": { transform: "scale(0.95)" },
                "&.Mui-selected": {
                  boxShadow: "0 2px 8px rgba(255,144,21,0.35)",
                },
              },
            }}
          />
        </Box>
      )}

      {/* Add subcontractor modal (mobile version) */}
      <MobileAddSubcontractorModal
        visible={displayAddModal}
        onHide={() => setDisplayAddModal(false)}
      />

      {/* Details modal (mobile) */}
      <MobileSubcontractorDetailsModal
        visible={displayDetailsModal}
        setVisible={setDisplayDetailsModal}
        subcontractor={onPreviewSubcontractor}
        setSubcontractor={setOnPreviewSubcontractor}
      />
    </Box>
  );
}
